import React, { useState, useMemo, useEffect } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import './ActivityTable.css';

function ActivityTable({ events }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showViolations, setShowViolations] = useState(true);
  const [showAssignments, setShowAssignments] = useState(false);
  const [showLostFound, setShowLostFound] = useState(true);
  const [showDamaged, setShowDamaged] = useState(true);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: 'Student ID',
        accessor: 'studentId',
      },
      {
        Header: 'Student Name',
        accessor: row => `${row.studentFirstName} ${row.studentLastName}`,
        id: 'studentName',
      },
      {
        Header: 'Event',
        accessor: 'event',
        Cell: ({ value }) => {
          if (!value) return '-';
          
          const eventLower = value.toLowerCase();
          let className = '';
          
          if (eventLower.includes('violation')) {
            className = 'event-violation';
          } else if (eventLower.includes('assigned')) {
            className = 'event-assigned';
          } else if (eventLower.includes('damaged') || eventLower.includes('undamaged')) {
            className = 'event-damaged';
          } else if (eventLower.includes('lost') || eventLower.includes('found')) {
            className = 'event-lost';
          }
          
          return <span className={className}>{value}</span>;
        },
      },
      {
        Header: 'Message',
        accessor: 'message',
      },
      {
        Header: 'Bag Serial #',
        accessor: 'bagSerialNumber',
        Cell: ({ value }) => value || '-',
      },
      {
        Header: 'Reported By',
        accessor: 'reportedByName',
        Cell: ({ value }) => value || '-',
      },
    ],
    []
  );

  // Filter events based on search query and event type filters
  const filteredEvents = useMemo(() => {
    let filtered = events;
    
    // Apply event type filters
    filtered = filtered.filter(event => {
      const eventLower = event.event ? event.event.toLowerCase() : '';
      
      const isViolation = eventLower.includes('violation');
      const isAssignment = eventLower.includes('assigned') || eventLower.includes('unassigned');
      const isLostFound = eventLower.includes('lost') || eventLower.includes('found');
      const isDamaged = eventLower.includes('damaged') || eventLower.includes('undamaged');
      
      // If no filter matches, show the event (for other event types)
      if (!isViolation && !isAssignment && !isLostFound && !isDamaged) {
        return true;
      }
      
      // Check if event matches any enabled filter
      if (isViolation && showViolations) return true;
      if (isAssignment && showAssignments) return true;
      if (isLostFound && showLostFound) return true;
      if (isDamaged && showDamaged) return true;
      
      return false;
    });
    
    // Apply search query (using debounced value)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(event => {
        const studentId = event.studentId ? event.studentId.toLowerCase() : '';
        const studentName = `${event.studentFirstName} ${event.studentLastName}`.toLowerCase();
        const message = event.message ? event.message.toLowerCase() : '';
        const eventType = event.event ? event.event.toLowerCase() : '';
        const bagSerial = event.bagSerialNumber ? event.bagSerialNumber.toLowerCase() : '';
        const reportedBy = event.reportedByName ? event.reportedByName.toLowerCase() : '';
        
        return studentId.includes(query) || studentName.includes(query) || 
               message.includes(query) || eventType.includes(query) || 
               bagSerial.includes(query) || reportedBy.includes(query);
      });
    }

    // Sort by date descending (most recent first)
    filtered = [...filtered].sort((a, b) => {
      return (b.date || 0) - (a.date || 0);
    });
    
    return filtered;
  }, [events, debouncedSearchQuery, showViolations, showAssignments, showLostFound, showDamaged]);

  // Use the useTable hook with sorting and pagination
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredEvents,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useSortBy,
    usePagination
  );

  // Reset to first page when filters change
  useEffect(() => {
    gotoPage(0);
  }, [debouncedSearchQuery, showViolations, showAssignments, showLostFound, showDamaged, gotoPage]);

  return (
    <>
      <div className="activity-header">
        <div className="activity-left">
          <div className="activity-title">
            <h3>Activity Log ({filteredEvents.length})</h3>
            <p className="activity-subtitle">Last 360 days</p>
          </div>
          <div className="event-filters">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showViolations}
                onChange={(e) => setShowViolations(e.target.checked)}
              />
              <span>Violations</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showAssignments}
                onChange={(e) => setShowAssignments(e.target.checked)}
              />
              <span>Assignments</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showLostFound}
                onChange={(e) => setShowLostFound(e.target.checked)}
              />
              <span>Lost/Found</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showDamaged}
                onChange={(e) => setShowDamaged(e.target.checked)}
              />
              <span>Damaged</span>
            </label>
          </div>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by student, event, message, or bag serial..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      {events.length === 0 ? (
        <div className="no-data">No activity found for this school</div>
      ) : filteredEvents.length === 0 ? (
        <div className="no-data">No activity matches your search</div>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="activity-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={j}>
                      <div className="th-content">
                        {column.render('Header')}
                        <span className="sort-indicator">
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' ↓'
                              : ' ↑'
                            : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {'<<'}
            </button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage}>
              {'<'}
            </button>
            <button onClick={() => nextPage()} disabled={!canNextPage}>
              {'>'}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              {'>>'}
            </button>
            <span>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
            <span>
              | Go to page:{' '}
              <input
                type="number"
                defaultValue={pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                  gotoPage(page)
                }}
                style={{ width: '100px' }}
              />
            </span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
            >
              {[10, 25, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );
}

export default ActivityTable;

